import { useEffect, useRef } from 'react';

export interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
  preloadImages?: Array<{
    href: string;
    media?: string;
    type?: string;
  }>;
}

/**
 * SEO component that manages document head metadata.
 *
 * Uses React 19 native <title> and <meta name> hoisting for standard tags.
 * Uses useEffect for OG property-based tags, canonical link, and JSON-LD
 * (since React 19 only hoists <meta name>, not <meta property>).
 *
 * Cleans up on unmount for proper SPA navigation.
 */
export function SEO({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage,
  ogUrl,
  twitterCard = 'summary_large_image',
  jsonLd,
  noindex = false,
  preloadImages = [],
}: SEOProps) {
  // Stabilize jsonLd to prevent unnecessary re-renders from object recreation
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : '';
  const jsonLdRef = useRef(jsonLdString);
  const stableJsonLd = useRef(jsonLd);
  if (jsonLdString !== jsonLdRef.current) {
    jsonLdRef.current = jsonLdString;
    stableJsonLd.current = jsonLd;
  }

  useEffect(() => {
    const managedElements: HTMLElement[] = [];

    function setMeta(attr: string, key: string, content: string) {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      managedElements.push(el);
      el.content = content;
    }

    // OG tags (property-based, not hoisted by React 19)
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', ogType);
    if (ogImage) setMeta('property', 'og:image', ogImage);
    if (ogUrl) setMeta('property', 'og:url', ogUrl);

    // Twitter tags
    setMeta('name', 'twitter:card', twitterCard);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    if (ogImage) setMeta('name', 'twitter:image', ogImage);

    // Canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      managedElements.push(link);
      link.href = canonical;
    }

    for (const preload of preloadImages) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = preload.href;
      if (preload.media) link.media = preload.media;
      if (preload.type) link.type = preload.type;
      document.head.appendChild(link);
      managedElements.push(link);
    }

    // Robots noindex
    if (noindex) {
      setMeta('name', 'robots', 'noindex,nofollow');
    }

    // JSON-LD
    const currentJsonLd = stableJsonLd.current;
    const jsonLdItems = currentJsonLd
      ? Array.isArray(currentJsonLd) ? currentJsonLd : [currentJsonLd]
      : [];

    const scriptElements: HTMLScriptElement[] = [];
    for (const item of jsonLdItems) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
      scriptElements.push(script);
    }

    return () => {
      for (const el of managedElements) {
        el.remove();
      }
      for (const script of scriptElements) {
        script.remove();
      }
    };
  }, [
    title,
    description,
    canonical,
    ogType,
    ogImage,
    ogUrl,
    twitterCard,
    jsonLdString,
    noindex,
    preloadImages,
  ]);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
    </>
  );
}
